import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Button, SimpleGrid, Avatar } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { TenantName } from '../../components/TenantName';
import { TwoLineCell } from '../../components/TwoLineCell';
import { TableFooter } from '../../components/TableFooter';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { StatCard } from '../../components/StatCard';
import { useTenantMap } from '../../data/useTenants';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatDate, formatInteger, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { useT } from '../../i18n/I18nProvider';
import {
  AUTO_DISABLE_AT, EventChips, FailureMeter, StateBadge, UrlCell, type Subscription
} from './components/parts';
import { SubscriptionDrawer } from './components/SubscriptionDrawer';
import './webhooks.css';

const MUTED = 'var(--rr-color-text-muted)';
const ROW_LIMIT = 500;

type KpiKey = 'all' | 'active' | 'disabled' | 'failing';

const LinkIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
    <path d="M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.6 1.6" />
    <path d="M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.6-1.6" />
  </svg>
);
const CheckIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
);
const PauseIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
    <path d="M9.5 5v14M14.5 5v14" />
  </svg>
);
const AlertIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M12 8v5M12 16.5v.5" />
    <circle cx="12" cy="12" r="8.5" />
  </svg>
);

/**
 * The organisation cell: the name with its slug beneath, never the GUID.
 *
 * Declared at module scope rather than inside the page, because a component
 * defined in a render body is a new component type on every render and React
 * remounts the whole cell each time the table redraws.
 */
function OrgCell({ id }: { id: string }) {
  const { map } = useTenantMap();
  const tenant = map.get(id);
  return (
    <TwoLineCell
      leading={<Avatar name={tenant?.name ?? '?'} size={28} />}
      primary={<TenantName id={id} />}
      secondary={tenant?.slug}
    />
  );
}

export default function WebhooksPage() {
  const nav = useNavigate();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'webhooks', 'subscriptions'],
    queryFn: () => admin.listWebhookSubscriptions({ limit: ROW_LIMIT })
  });
  const all = useMemo(() => (data?.data ?? []) as Subscription[], [data]);
  const { map: tenants } = useTenantMap();
  const denied = isApiError(error) && error.status === 403;
  const capped = all.length >= ROW_LIMIT;
  const failed = Boolean(error);

  const [view, setView] = usePersistedState<ViewMode>('admin.webhooks.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.webhooks.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Subscription | null>(null);

  /*
   * The deliveries page links back here for one endpoint. Opening that link
   * straight into the detail panel closes the loop, rather than dropping the
   * operator on a 500-row list holding an id they now have to search for.
   */
  const wantedId = params.get('subscription');
  useEffect(() => {
    if (!wantedId) return;
    const match = all.find((r) => r.id === wantedId);
    if (match) {
      setDetail(match);
      setParams(new URLSearchParams(), { replace: true });
    }
  }, [wantedId, all, setParams]);

  const counts = useMemo(() => ({
    all: all.length,
    active: all.filter((r) => r.is_active).length,
    disabled: all.filter((r) => !r.is_active).length,
    failing: all.filter((r) => r.failure_count > 0).length,
    orgs: new Set(all.map((r) => r.organisation_id)).size
  }), [all]);

  const share = (n: number) => (counts.all === 0 ? '0.0' : ((n / counts.all) * 100).toFixed(1));

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('webhooks.kpi.all'), value: counts.all, tone: 'primary' },
    { id: 'active', label: t('status.active'), value: counts.active, tone: 'healthy' },
    { id: 'disabled', label: t('status.disabled'), value: counts.disabled, tone: 'inactive' },
    { id: 'failing', label: t('webhooks.kpi.failing'), value: counts.failing, tone: 'warning' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // Tenants are picked by name. A dropdown of GUID prefixes is a dropdown
    // nobody can use.
    const orgs = Array.from(new Set(all.map((r) => r.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const events = Array.from(new Set(all.flatMap((r) => r.event_types ?? []))).sort();
    return [
      { key: 'organisation', label: t('webhooks.filters.organisation'), placeholder: t('webhooks.filters.organisationPlaceholder'), options: orgs },
      { key: 'event', label: t('webhooks.filters.event'), placeholder: t('webhooks.filters.eventPlaceholder'), options: events.map((e) => ({ value: e, label: e })) },
      { key: 'status', label: t('common.status'), placeholder: t('webhooks.filters.statusPlaceholder'), options: [{ value: 'active', label: t('status.active') }, { value: 'disabled', label: t('status.disabled') }] }
    ];
  }, [all, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;
  function clearAllFilters() { setQuery(''); setKpi('all'); setFilters({}); setPage(1); }

  /*
   * What is narrowing the list, each removable on its own. A count badge on the
   * Filters button says how many are on but not which, so a short list gives an
   * operator no way to tell whether the data is small or a forgotten filter is
   * still applied.
   */
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    const item = kpiItems.find((k) => k.id === kpi);
    pills.push({ id: 'kpi', field: t('common.status'), value: item?.label ?? kpi });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('webhooks.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.event) pills.push({ id: 'event', field: t('webhooks.filters.event'), value: filters.event });
  if (filters.status) {
    pills.push({ id: 'status', field: t('common.status'), value: filters.status === 'active' ? t('status.active') : t('status.disabled') });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi === 'active') rows = rows.filter((r) => r.is_active);
    if (kpi === 'disabled') rows = rows.filter((r) => !r.is_active);
    if (kpi === 'failing') rows = rows.filter((r) => r.failure_count > 0);
    if (filters.organisation) rows = rows.filter((r) => r.organisation_id === filters.organisation);
    if (filters.event) rows = rows.filter((r) => (r.event_types ?? []).includes(filters.event!));
    if (filters.status === 'active') rows = rows.filter((r) => r.is_active);
    if (filters.status === 'disabled') rows = rows.filter((r) => !r.is_active);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const tenant = tenants.get(r.organisation_id);
        return `${r.url} ${tenant?.name ?? ''} ${tenant?.slug ?? ''}`.toLowerCase().includes(q);
      });
    }
    return rows;
  }, [all, kpi, filters, query, tenants]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('system.hub.throughput.title')}
        title={t('webhooks.title')}
        description={t('webhooks.description', { threshold: AUTO_DISABLE_AT })}
        actions={
          <Group gap="sm">
            {dataUpdatedAt > 0 && (
              <Text role="caption" c={MUTED}>
                {t('webhooks.lastUpdated', { when: formatRelative(new Date(dataUpdatedAt).toISOString()) })}
              </Text>
            )}
            <Button size="sm" variant="default" onClick={() => refetch()} loading={isFetching}>{t('action.refresh')}</Button>
            <Button size="sm" variant="subtle" onClick={() => nav('/webhooks/deliveries')}>{t('webhooks.openDeliveries')}</Button>
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('webhooks.denied.title')}>{t('webhooks.denied.body')}</Alert>
      )}

      {/* A query that failed is not a platform with no webhooks, so every card
          says "n/a" rather than printing a confident zero. */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          icon={LinkIcon}
          tone="primary"
          label={t('webhooks.kpi.all')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.all)}
          hint={failed ? undefined : t('webhooks.stats.allHint', { count: formatInteger(counts.orgs) })}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={CheckIcon}
          tone="success"
          label={t('status.active')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.active)}
          hint={failed ? undefined : t('webhooks.stats.percentOfTotal', { percent: share(counts.active) })}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={PauseIcon}
          tone="neutral"
          label={t('status.disabled')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.disabled)}
          hint={failed ? undefined : t('webhooks.stats.inactiveHint')}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={AlertIcon}
          tone="error"
          label={t('webhooks.stats.failingLabel')}
          value={failed ? t('common.notAvailable') : formatInteger(counts.failing)}
          hint={failed ? undefined : t('webhooks.stats.percentOfTotal', { percent: share(counts.failing) })}
          loading={isLoading}
          withRule
        />
      </SimpleGrid>

      <TableShell
        title={t('webhooks.tableTitle')}
        totalLabel={
          capped
            ? t('webhooks.totalLabelCapped', { shown: filtered.length, total: all.length })
            : t('webhooks.totalLabel', { shown: filtered.length, total: all.length })
        }
        kpis={<KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('webhooks.searchPlaceholder')}
        filters={
          <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={!isLoading && !error && filtered.length === 0}
        empty={<EmptyState title={t('webhooks.empty')} description={hasActiveFilters ? t('webhooks.emptyFiltered') : t('webhooks.emptyHint')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={error && !denied ? (
          <Alert color="error">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <span>{t('webhooks.loadError')}</span>
              <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={capped}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <Stack gap="md">
          <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

          {view === 'table' && (
            <DataTable<Indexed<Subscription>>
              records={paged as Indexed<Subscription>[]}
              loading={isLoading}
              idAccessor="id"
              emptyState={t('webhooks.emptyHint')}
              onRowClick={(r) => setDetail(r)}
              columns={[
                { accessor: 'organisation_id', title: t('webhooks.columns.org'), render: (r) => <OrgCell id={r.organisation_id} /> },
                { accessor: 'url', title: t('webhooks.columns.url'), render: (r) => <UrlCell url={r.url} maxWidth={280} /> },
                { accessor: 'event_types', title: t('webhooks.columns.events'), render: (r) => <EventChips events={r.event_types ?? []} max={2} /> },
                { accessor: 'is_active', title: t('common.status'), render: (r) => <StateBadge r={r} t={t} /> },
                {
                  accessor: 'failure_count',
                  title: t('webhooks.columns.failures', { threshold: AUTO_DISABLE_AT }),
                  render: (r) => <FailureMeter r={r} t={t} />
                },
                {
                  accessor: 'created_at',
                  title: t('webhooks.columns.created'),
                  sortable: true,
                  render: (r) => <TwoLineCell primary={formatDate(r.created_at)} secondary={formatRelative(r.created_at)} />
                },
                {
                  accessor: 'id',
                  title: t('common.actions'),
                  // The question after "which endpoint is failing" is always
                  // "what did the last attempt return", and until now the two
                  // pages had no link between them.
                  render: (r) => (
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={(e) => { e.stopPropagation(); nav(`/webhooks/deliveries?subscription_id=${r.id}`); }}
                    >
                      {t('webhooks.viewDeliveries')}
                    </Button>
                  )
                }
              ]}
            />
          )}
          {view === 'list' && (
            <Stack gap="xs">
              {paged.map((r) => (
                <button key={r.id} type="button" onClick={() => setDetail(r)} className="rr-list-row" aria-label={t('webhooks.openDetail')}>
                  <Group gap="md" align="center" wrap="wrap" style={{ width: '100%' }}>
                    <Stack gap={0} style={{ flex: 1, minWidth: 220 }}>
                      <Text role="subtitle" fw={600} truncate>{r.url}</Text>
                      <Group gap="xs" align="baseline" wrap="wrap">
                        <TenantName id={r.organisation_id} subtle />
                        <Text role="caption" c={MUTED}>
                          {t((r.event_types ?? []).length === 1 ? 'webhooks.list.metaOne' : 'webhooks.list.metaOther', { count: (r.event_types ?? []).length, when: formatRelative(r.created_at) })}
                        </Text>
                      </Group>
                    </Stack>
                    <FailureMeter r={r} t={t} />
                    <StateBadge r={r} t={t} />
                  </Group>
                </button>
              ))}
            </Stack>
          )}
          {view === 'cards' && (
            <EntityCardGrid<Subscription>
              records={paged}
              idAccessor="id"
              cols={3}
              spacing="md"
              onCardClick={(r) => setDetail(r)}
              renderCard={(r) => (
                <Stack gap="md">
                  <Group justify="space-between" align="baseline" wrap="nowrap">
                    <TenantName id={r.organisation_id} />
                    <StateBadge r={r} t={t} />
                  </Group>
                  <Text role="bodySmall" fw={600} truncate title={r.url}>{r.url}</Text>
                  <EventChips events={r.event_types ?? []} max={4} />
                  <Group justify="space-between" align="center" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
                    <Text role="caption" c={MUTED}>{t('webhooks.createdRelative', { when: formatRelative(r.created_at) })}</Text>
                    <FailureMeter r={r} t={t} />
                  </Group>
                </Stack>
              )}
            />
          )}
        </Stack>
      </TableShell>

      <SubscriptionDrawer row={detail} onClose={() => setDetail(null)} />
    </Stack>
  );
}
